( function () {
	class vanillaJQ {
		constructor( selector, parent = document ) {
			// Define el as an empty array
			this.el = [];

			// HANDLE: _s(""), _s(null), _s(undefined), _s(false), _s([])
			if ( ! selector || ( Array.isArray( selector ) && selector.length === 0 ) ) {
				return this;
			}

			if ( typeof selector === 'object' ) {
				// Check if the selector is a DOM element or a NodeList/Array of elements
				if ( selector instanceof Element ) {
					// Directly wrap the DOM element in an array
					this.el = [ selector ];
				} else if ( selector instanceof NodeList || Array.isArray( selector ) ) {
					// Convert NodeList or Array of elements directly to Array
					this.el = Array.from( selector );
				} else {
					// For any other object input, wrap it in an array (though this case might need further refinement based on expected input types)
					this.el = [ selector ];
				}
			} else if ( typeof selector === 'string' ) {
				// Use querySelectorAll for string selectors to get all matching elements
				this.el = parent.querySelectorAll( selector );
			}

			this.el = Array.from( this.el ); // Ensure el is always an Array
		}

		/**
		 * Equivalent to jQuery's ready() function.
		 *
		 * @see https://gomakethings.com/a-native-javascript-equivalent-of-jquerys-ready-method/
		 * @param {Function} fn - The callback function.
		 * @return {void}
		 */
		ready( fn ) {
			if ( typeof fn !== 'function' ) return;
			if ( document.readyState === 'complete' ) return fn();
			document.addEventListener( 'DOMContentLoaded', fn, false );
		}

		/**
		 * Gets the number of elements in the set of matched elements.
		 *
		 * @return {number} The number of elements.
		 */
		get length() {
			return this.el.length;
		}

		/**
		 * Run a callback against located elements.
		 *
		 * @param {Function} callback - The callback we want to run on each element.
		 * @return {Object} - returns the vanillaJQ object to allow chaining methods.
		 */
		each( callback ) {
			this.el.forEach( callback );
			return this;
		}

		/**
		 * Finds descendants of each element in the current set of matched elements, filtered by a selector.
		 *
		 * @param {string} selector - The CSS selector to match the elements against.
		 * @return {Object} - Returns a new vanillaJQ object containing the found descendants.
		 */
		find( selector ) {
			const foundElements = [];
			this.each( el => {
				// Handle child combinator selectors
				if ( selector.startsWith( '>' ) ) {
					const childSelector = selector.replace( '>', '' ).trim();
					Array.from( el.children ).forEach( child => {
						if ( child.matches( childSelector ) ) {
							foundElements.push( child );
						}
					} );
				} else {
					foundElements.push( ...el.querySelectorAll( selector ) );
				}
			} );
			return new vanillaJQ( foundElements );
		}

		/**
		 * Add one or more class names to each element in the set of matched elements.
		 *
		 * @param {string} classNames - One or more class names to be added to the elements, separated by spaces.
		 * @return {Object} - Returns the vanillaJQ object to allow chaining methods.
		 */
		addClass( classNames ) {
			const classList = classNames.split( ' ' ); // Split the class names by spaces
			this.each( el => {
				classList.forEach( className => {
					if ( className.trim() ) {
						// Check if className is not just whitespace
						el.classList.add( className );
					}
				} );
			} );
			return this;
		}

		/**
		 * Remove a class from elements.
		 *
		 * @param {string} className - The class-name we want to add to element(s).
		 * @return {Object} - Returns the vanillaJQ object to allow chaining methods.
		 */
		removeClass( className ) {
			this.each( el => el.classList.remove( className ) );
			return this;
		}

		/**
		 * Convert a string to camelCase.
		 *
		 * @param {string} string The string we want to convert.
		 * @return {string} - Returns the string formatted in camelCase.
		 */
		static camelCase( string ) {
			return string.replace( /-([a-z])/g, ( _all, letter ) => letter.toUpperCase() );
		}

		/**
		 * Get the styles for a property, or change the value if one is defined.
		 *
		 * @param {string}           property - The CSS property we're referencing.
		 * @param {string|undefined} value    - The value we want to assign, or undefined if we want to get the value.
		 * @return {Object|string} - returns the vanillaJQ object to allow chaining methods, OR the value if 2nd arg is undefined.
		 */
		css( property, value ) {
			if ( typeof value === 'undefined' ) {
				return getComputedStyle( this.el[ 0 ] )[ property ];
			}
			this.each( el => ( el.style[ vanillaJQ.camelCase( property ) ] = value ) );
			return this;
		}

		/**
		 * Figure out if the element has a class or not.
		 *
		 * @param {string} className - The class-name we're looking for.
		 * @return {boolean} - Whether the element has the class we need or not.
		 */
		hasClass( className ) {
			return this.el.some( el => el.classList.contains( className ) );
		}

		/**
		 * Hide the element(s).
		 *
		 * @return {Object} - returns the vanillaJQ object to allow chaining methods.
		 */
		hide() {
			this.each( el => ( el.style.display = 'none' ) );
			return this;
		}

		/**
		 * Show the element(s)
		 *
		 * @param {string} display - The css-display mode. Defaults to "block".
		 * @return {Object} - returns the vanillaJQ object to allow chaining methods.
		 */
		show( display = 'block' ) {
			this.each( el => ( el.style.display = display ) );
			return this;
		}

		/**
		 * Gets the first element that matches the selector by testing the element itself and traversing up through its ancestors.
		 *
		 * @param {string} selector - The CSS selector to match the elements against.
		 * @return {Object} - Returns a new vanillaJQ object containing the closest matching element.
		 */
		closest( selector ) {
			return new vanillaJQ( this.el.map( el => el.closest( selector ) ).filter( Boolean ) );
		}

		/**
		 * Get the direct parents of elements.
		 *
		 * @return {Object} - Returns the vanillaJQ object to allow chaining methods.
		 */
		parent() {
			return new vanillaJQ( this.el.map( el => el.parentNode ) );
		}

		/**
		 * Get the parents based on a selector.
		 *
		 * @param {string} selector - The CSS selector.
		 * @return {Object} - returns the vanillaJQ object to allow chaining methods.
		 */
		parents( selector ) {
			return new vanillaJQ( this.el.flatMap( el => el.closest( selector ) ) );
		}

		/**
		 * Handle events.
		 *
		 * @param {Function} method   - for example addEventListener.
		 * @param {string}   events   - The JS event we want to add a listener for.
		 * @param {Function} listener - The function to add to the listener.
		 * @return {Object} - returns the vanillaJQ object to allow chaining methods.
		 */
		eventsHandler( method, events, listener ) {
			events
				.split( ' ' )
				.forEach( event => this.each( el => method.call( el, event, listener, false ) ) );
			return this;
		}

		/**
		 * Similar to jQuery.on().
		 *
		 * @param {string}   event    - The JS event we want to add a listener for.
		 * @param {Function} listener - The function to add to the listener.
		 * @return {void}
		 */
		on( event, listener ) {
			return this.eventsHandler( addEventListener, event, listener );
		}

		/**
		 * Similar to jQuery.off().
		 *
		 * @param {string}   event    - The JS event we want to add a listener for.
		 * @param {Function} listener - The function to add to the listener.
		 * @return {void}
		 */
		off( event, listener ) {
			this.eventsHandler( removeEventListener, event, listener );
		}

		/**
		 * Triggers a specified event on the element(s) and optionally passes additional data.
		 *
		 * @param {string} eventType         - The type of the event (e.g., 'click', 'change').
		 * @param {any}    [extraParameters] - Additional data to be passed with the event.
		 * @return {Object} - Returns the vanillaJQ object to allow chaining methods.
		 */
		trigger( eventType, extraParameters ) {
			const event = new CustomEvent( eventType, {
				detail: extraParameters,
			} );
			this.each( el => el.dispatchEvent( event ) );
			return this;
		}

		/**
		 * Gets the current coordinates of the first element in the set of matched elements, relative to the document.
		 *
		 * @return {Object} An object containing the properties `top` and `left`.
		 */
		offset() {
			if ( ! this.el[ 0 ] ) return { top: 0, left: 0 };

			const rect = this.el[ 0 ].getBoundingClientRect();
			return {
				top: rect.top + window.scrollY,
				left: rect.left + window.scrollX,
			};
		}

		/**
		 * Gets the current computed height for the first element in the set of matched elements, excluding padding, border, and margin.
		 *
		 * @return {number} The height of the element.
		 */
		height() {
			if ( ! this.el[ 0 ] ) return 0;
			return this.el[ 0 ].clientHeight;
		}

		/**
		 * Gets the current computed width for the first element in the set of matched elements, excluding padding, border, and margin.
		 *
		 * @return {number} The width of the element.
		 */
		width() {
			if ( ! this.el[ 0 ] ) return 0;
			return this.el[ 0 ].clientWidth;
		}

		/**
		 * Gets the current computed outer height (including padding and border, and optionally margin)
		 * for the first element in the set of matched elements.
		 *
		 * @param {boolean} [includeMargin=false] - Whether to include margin in the calculation.
		 * @return {number} The outer height of the element.
		 */
		outerHeight( includeMargin = false ) {
			if ( ! this.el[ 0 ] ) return 0;

			let height = this.el[ 0 ].offsetHeight;

			if ( includeMargin ) {
				const style = getComputedStyle( this.el[ 0 ] );
				height += parseInt( style.marginTop ) + parseInt( style.marginBottom );
			}

			return height;
		}

		/**
		 * Gets the current computed outer width (including padding and border, and optionally margin)
		 * for the first element in the set of matched elements.
		 *
		 * @param {boolean} [includeMargin=false] - Whether to include margin in the calculation.
		 * @return {number} The outer width of the element.
		 */
		outerWidth( includeMargin = false ) {
			if ( ! this.el[ 0 ] ) return 0;

			let width = this.el[ 0 ].offsetWidth;

			if ( includeMargin ) {
				const style = getComputedStyle( this.el[ 0 ] );
				width += parseInt( style.marginLeft ) + parseInt( style.marginRight );
			}

			return width;
		}

		/**
		 * Toggles a class name on each element in the set of matched elements.
		 *
		 * @param {string}  className - The class name to toggle.
		 * @param {boolean} [state]   - A Boolean (not just truthy/falsy) value to determine whether the class should be added or removed.
		 * @return {Object} - Returns the vanillaJQ object to allow chaining methods.
		 */
		toggleClass( className, state ) {
			this.each( el => {
				if ( typeof state !== 'undefined' ) {
					if ( state ) {
						el.classList.add( className );
					} else {
						el.classList.remove( className );
					}
				} else {
					el.classList.toggle( className );
				}
			} );
			return this;
		}

		/**
		 * Gets the value of an attribute for the first element in the set of matched elements
		 * or sets one or more attributes for every matched element.
		 *
		 * @param {string|Object} attributeName - The name of the attribute to get or set, or an object of attribute-value pairs to set.
		 * @param {string}        [value]       - The value to set for the attribute.
		 * @return {Object|string} - Returns the value of the attribute for a getter call, or the vanillaJQ object for setter calls.
		 */
		attr( attributeName, value ) {
			if ( typeof attributeName === 'string' && value === undefined ) {
				// Get attribute
				return this.el.length > 0 ? this.el[ 0 ].getAttribute( attributeName ) : undefined;
			}

			// Set attributes
			if ( typeof attributeName === 'object' ) {
				for ( const key in attributeName ) {
					if ( attributeName.hasOwnProperty( key ) ) {
						this.each( el => el.setAttribute( key, attributeName[ key ] ) );
					}
				}
			} else {
				this.each( el => el.setAttribute( attributeName, value ) );
			}

			return this;
		}

		/**
		 * Sets focus on the first element in the set of matched elements.
		 *
		 * @return {Object} - Returns the vanillaJQ object to allow chaining methods.
		 */
		focus() {
			if ( this.el.length > 0 && this.el[ 0 ].focus ) {
				this.el[ 0 ].focus();
			}
			return this;
		}

		/**
		 * Removes an attribute from all elements in the set of matched elements.
		 *
		 * @param {string} attributeName - The name of the attribute to remove.
		 * @return {Object} - Returns the vanillaJQ object to allow chaining methods.
		 */
		removeAttr( attributeName ) {
			this.each( el => el.removeAttribute( attributeName ) );
			return this;
		}

		/**
		 * Appends content to the end of each element in the set of matched elements.
		 *
		 * @param {string|Element|NodeList} content - The content to append (could be an HTML string, DOM element, or collection of DOM elements).
		 * @return {Object} - Returns the vanillaJQ object to allow chaining methods.
		 */
		append( content ) {
			this.each( el => {
				if ( typeof content === 'string' ) {
					el.insertAdjacentHTML( 'beforeend', content );
				} else if ( content instanceof Node ) {
					el.appendChild( content.cloneNode( true ) );
				} else if ( content instanceof NodeList ) {
					content.forEach( node => el.appendChild( node.cloneNode( true ) ) );
				}
			} );
			return this;
		}

		/**
		 * Prepends content to the beginning of each element in the set of matched elements.
		 *
		 * @param {string|Element|NodeList} content - The content to prepend (can be an HTML string, DOM element, or collection of DOM elements).
		 * @return {Object} - Returns the vanillaJQ object to allow chaining methods.
		 */
		prepend( content ) {
			this.each( el => {
				if ( typeof content === 'string' ) {
					el.insertAdjacentHTML( 'afterbegin', content );
				} else if ( content instanceof Node ) {
					el.insertBefore( content.cloneNode( true ), el.firstChild );
				} else if ( content instanceof NodeList ) {
					Array.from( content )
						.reverse()
						.forEach( node =>
							el.insertBefore( node.cloneNode( true ), el.firstChild )
						);
				}
			} );
			return this;
		}

		/**
		 * Gets the combined text contents of each element in the set of matched elements,
		 * or sets the text content for every matched element.
		 *
		 * @param {string} [textToSet] - The text to set for each element. If not provided, the current text will be returned.
		 * @return {Object|string} - Returns the combined text when getting, or the vanillaJQ object for chaining when setting.
		 */
		text( textToSet ) {
			if ( textToSet === undefined ) {
				// Get text
				return this.el.reduce( ( combinedText, el ) => combinedText + el.textContent, '' );
			}
			// Set text
			this.each( el => {
				el.textContent = textToSet;
			} );
			return this;
		}

		/**
		 * Gets the HTML content of the first element in the set of matched elements,
		 * or sets the HTML content for every matched element.
		 *
		 * @param {string} [htmlToSet] - The HTML content to set for each element. If not provided, the current HTML of the first element will be returned.
		 * @return {Object|string} - Returns the HTML content when getting, or the vanillaJQ object for chaining when setting.
		 */
		html( htmlToSet ) {
			if ( htmlToSet === undefined ) {
				// Get HTML
				return this.el.length > 0 ? this.el[ 0 ].innerHTML : undefined;
			}
			// Set HTML
			this.each( el => {
				el.innerHTML = htmlToSet;
			} );
			return this;
		}

		/**
		 * Removes the set of matched elements from the DOM.
		 *
		 * @return {Object} - Returns the vanillaJQ object to allow chaining methods before the removal.
		 */
		remove() {
			this.each( el => {
				if ( el.parentNode ) {
					el.parentNode.removeChild( el );
				}
			} );
			return this;
		}

		/**
		 * Wraps an HTML structure around each element in the set of matched elements.
		 *
		 * @param {string} wrappingElement - The HTML structure to wrap around each element (e.g., '<div></div>').
		 * @return {Object} - Returns the vanillaJQ object to allow chaining methods.
		 */
		wrap( wrappingElement ) {
			this.each( el => {
				// Create the wrapper element from the HTML string
				const wrapper = document.createElement( 'div' );
				wrapper.innerHTML = wrappingElement;
				const wrapperInner = wrapper.firstChild;

				// Insert wrapper before el in the DOM tree
				el.parentNode.insertBefore( wrapperInner, el );

				// Move el into wrapper
				wrapperInner.appendChild( el );
			} );
			return this;
		}

		/**
		 * Wraps an HTML structure around all elements in the set of matched elements as a single group.
		 *
		 * @param {string} wrappingElement - The HTML structure to wrap around the set of elements (e.g., '<div></div>').
		 * @return {Object} - Returns the vanillaJQ object to allow chaining methods.
		 */
		wrapAll( wrappingElement ) {
			if ( this.el.length === 0 ) return this;

			// Create the wrapper element from the HTML string
			const wrapper = document.createElement( 'div' );
			wrapper.innerHTML = wrappingElement;
			const wrapperInner = wrapper.firstChild;

			// Insert the wrapper in the DOM before the first element
			const firstElement = this.el[ 0 ];
			firstElement.parentNode.insertBefore( wrapperInner, firstElement );

			// Move each element into the wrapper
			this.each( el => {
				wrapperInner.appendChild( el );
			} );

			return this;
		}

		/**
		 * Inserts content before each element in the set of matched elements.
		 *
		 * @param {string|Element|NodeList} content - The content to insert (can be an HTML string, DOM element, or collection of DOM elements).
		 * @return {Object} - Returns the vanillaJQ object to allow chaining methods.
		 */
		before( content ) {
			this.each( el => {
				if ( typeof content === 'string' ) {
					el.insertAdjacentHTML( 'beforebegin', content );
				} else if ( content instanceof Node ) {
					el.parentNode.insertBefore( content.cloneNode( true ), el );
				} else if ( content instanceof NodeList ) {
					Array.from( content ).forEach( node => {
						el.parentNode.insertBefore( node.cloneNode( true ), el );
					} );
				}
			} );
			return this;
		}

		/**
		 * Inserts content after each element in the set of matched elements.
		 *
		 * @param {string|Element|NodeList} content - The content to insert (can be an HTML string, DOM element, or collection of DOM elements).
		 * @return {Object} - Returns the vanillaJQ object to allow chaining methods.
		 */
		after( content ) {
			this.each( el => {
				if ( typeof content === 'string' ) {
					el.insertAdjacentHTML( 'afterend', content );
				} else if ( content instanceof Node ) {
					el.parentNode.insertBefore( content.cloneNode( true ), el.nextSibling );
				} else if ( content instanceof NodeList ) {
					Array.from( content )
						.reverse()
						.forEach( node => {
							el.parentNode.insertBefore( node.cloneNode( true ), el.nextSibling );
						} );
				}
			} );
			return this;
		}

		/**
		 * Replaces each element in the set of matched elements with the provided new content.
		 *
		 * @param {string|Element|NodeList} newContent - The new content to replace the matched elements (can be an HTML string, DOM element, or collection of DOM elements).
		 * @return {Object} - Returns the vanillaJQ object to allow chaining methods.
		 */
		replaceWith( newContent ) {
			this.each( el => {
				if ( typeof newContent === 'string' ) {
					el.insertAdjacentHTML( 'beforebegin', newContent );
					el.remove();
				} else if ( newContent instanceof Node ) {
					el.parentNode.replaceChild( newContent.cloneNode( true ), el );
				} else if ( newContent instanceof NodeList ) {
					Array.from( newContent ).forEach( node => {
						el.parentNode.insertBefore( node.cloneNode( true ), el );
					} );
					el.remove();
				}
			} );
			return this;
		}

		/**
		 * Creates a new vanillaJQ object with the current set of matched elements plus the additional elements.
		 *
		 * @param {string|Element|NodeList} selector - A CSS selector, HTML string, or Element to add to the set of matched elements.
		 * @return {Object} - Returns a new vanillaJQ object.
		 */
		add( selector ) {
			let additionalElements = [];
			if ( typeof selector === 'string' ) {
				additionalElements = document.querySelectorAll( selector );
			} else if ( selector instanceof Element ) {
				additionalElements = [ selector ];
			} else if ( selector instanceof NodeList ) {
				additionalElements = Array.from( selector );
			}

			const combinedElements = this.el.concat( additionalElements );
			return new vanillaJQ( combinedElements );
		}

		/**
		 * Reduces the set of matched elements to those that match the provided test.
		 *
		 * @param {string|Function} test - A CSS selector string or a function used as a test for each element in the set.
		 * @return {Object} - Returns a new vanillaJQ object with the filtered elements.
		 */
		filter( test ) {
			let filteredElements = [];

			if ( typeof test === 'string' ) {
				// Filter using a CSS selector
				filteredElements = this.el.filter( el => el.matches( test ) );
			} else if ( typeof test === 'function' ) {
				// Filter using a provided function
				filteredElements = this.el.filter( ( el, index ) => test.call( el, index, el ) );
			}

			return new vanillaJQ( filteredElements );
		}

		/**
		 * Retrieves the first DOM element from the matched elements set.
		 * This method returns the actual DOM element, not a vanillaJQ object.
		 * If the matched set is empty, it returns false instead of an element.
		 *
		 * @return {Element|boolean} The first element of the matched set or false if empty.
		 */
		firstElement() {
			return this.el.length > 0 ? this.el[ 0 ] : false;
		}
	}

	window._s = ( selector, parent ) => new vanillaJQ( selector, parent );

	window._s.fn = vanillaJQ.prototype;
} )();
